import { request } from './client';
import type { PageResult } from './catalog';

/* ============================================================
 * /admin/rbac/me — 当前操作员视图
 * ========================================================== */

export type MenuType = 'DIRECTORY' | 'MENU' | 'BUTTON';
export type MenuStatus = 'ACTIVE' | 'DISABLED';

export interface Menu {
  id: number;
  parent_id: number;
  type: MenuType;
  name: string;
  path?: string;
  component?: string;
  icon?: string;
  permission_code?: string;
  sort_order?: number;
  /** 0 隐藏 / 1 显示 */
  visible?: number;
  status?: MenuStatus;
  children?: Menu[];
}

/**
 * 管理员数据范围：PLATFORM 看全部数据；COMPANY 只看所属公司（= 名下品牌）的数据。
 * 后端在接口层收窄，前端只负责隐藏平台专用入口（见 stores/permissions 的 PLATFORM_ONLY_*）。
 */
export type AdminDataScope = 'PLATFORM' | 'COMPANY';

export interface MeRbacView {
  super_admin: boolean;
  /** SUPER_ADMIN 恒为 PLATFORM；旧后端不返回时按 PLATFORM 处理 */
  data_scope?: AdminDataScope;
  /** COMPANY 时为所属公司 ID，PLATFORM 为 null */
  company_id?: number | null;
  role_codes: string[];
  /** 用户可见的菜单树（DIRECTORY → MENU → BUTTON）。权限码挂在 BUTTON 节点的 permission_code 上 */
  menus: Menu[];
}

/** /admin/rbac/me 完整视图（菜单树 + 角色） */
export const fetchMyRbac = () =>
  request<MeRbacView>({ url: '/admin/rbac/me', method: 'GET' });

/** /admin/rbac/me/menus 仅菜单树（导航用快捷接口） */
export const fetchMyMenus = () =>
  request<Menu[]>({ url: '/admin/rbac/me/menus', method: 'GET' });

/**
 * 递归收集菜单树里所有非空 permission_code（按钮 / 区块 gate 用）。
 *
 * ⚠️ /admin/rbac/me 的菜单树里混着两种节点，JSON 上长得一模一样、没有任何标记：
 *   1. 真正被授权的节点（admin_role_menu 里有记录）；
 *   2. 后端为了让树能渲染，把已授权节点的父链无条件补进来的「脚手架」节点。
 * 而 342 适配目标 / 350 预装应用组 / 352 SN 方案 / 360 系统配置项 / 368 批次 RMA /
 * 376 配置下发任务 / 377 预装应用 这批较新的菜单，把 :read 码挂在 MENU 节点自身
 * （不再另建"查看"按钮）。所以这里理论上会把脚手架节点的 :read 误当成已授权。
 *
 * 明知有歧义仍然全收，是权衡后的选择：
 *   - 误收（授了写没授读，却以为有读）的代价：看到入口，点进去接口 403。而且这个
 *     组合已经在源头堵住了——角色授权面板勾写权限会自动带上对应的 :read
 *     （见 pages/rbac/roles 的 withImpliedReads），新授的角色不会再出现。
 *   - 漏收的代价：**授权了却看不到**，用户毫无办法自救。曾经试过按"有 children 的
 *     MENU 上的码一律不收"来保守处理，结果是把 360 授全了的角色依然看不到设备详情的
 *     「配置项」tab——比误收严重得多。
 * 前端权限只负责 UX（少渲染一个按钮），真鉴权在后端，宁可误放行也不能误拦截。
 *
 * TODO(后端)：给节点加 granted 标记，或把补出来的脚手架节点 permission_code 置空。
 * 那之后这里就没有歧义了，{@link menuTreeCoversPath} 那层兜底也可以撤掉。
 */
export function collectPermissionCodes(menus: Menu[]): string[] {
  const codes: string[] = [];
  const walk = (items: Menu[]) => {
    for (const m of items) {
      if (m.permission_code) codes.push(m.permission_code);
      if (m.children?.length) walk(m.children);
    }
  };
  walk(menus);
  return codes;
}

/**
 * /me 菜单树里是否有节点覆盖这个路由。
 *
 * 用途：路由 gate 的兜底。菜单树里出现某个 MENU——不管它是被直接授权的还是脚手架——
 * 都意味着「该用户在这个页面下至少有一项权限」，否则后端根本不会把它拼进树里。
 * 那就该让他进页面；具体接口有没有权限由后端说了算（该 403 就 403）。
 * 典型场景：页面的 :read 是一个独立 BUTTON（如设备列表的 admin:device:read），
 * 角色只授了写权限没授它——历史脏数据里有一批，别把人挡在页面外面。
 *
 * 详情页（/xxx/:id）用前缀匹配命中列表页那条菜单。
 */
export function menuTreeCoversPath(menus: Menu[], pathname: string): boolean {
  const hit = (items: Menu[]): boolean =>
    items.some((m) => {
      const p = m.path?.trim();
      if (p && (pathname === p || pathname.startsWith(`${p}/`))) return true;
      return m.children?.length ? hit(m.children) : false;
    });
  return hit(menus);
}

/* ============================================================
 * /admin/rbac/menus — 菜单管理（SUPER_ADMIN）
 * ========================================================== */

export interface MenuCreateRequest {
  parent_id: number;
  type: MenuType;
  name: string;
  path?: string;
  component?: string;
  icon?: string;
  permission_code?: string;
  sort_order?: number;
  visible?: number;
  status?: MenuStatus;
}

export type MenuUpdateRequest = Omit<MenuCreateRequest, 'parent_id' | 'type'> & {
  parent_id?: number;
};

export const listMenuTree = () =>
  request<Menu[]>({ url: '/admin/rbac/menus/tree', method: 'GET' });

export const getMenu = (id: number) =>
  request<Menu>({ url: `/admin/rbac/menus/${id}`, method: 'GET' });

export const createMenu = (data: MenuCreateRequest) =>
  request<Menu>({ url: '/admin/rbac/menus', method: 'POST', data });

export const updateMenu = (id: number, data: MenuUpdateRequest) =>
  request<Menu>({ url: `/admin/rbac/menus/${id}`, method: 'PUT', data });

export const deleteMenu = (id: number) =>
  request<void>({ url: `/admin/rbac/menus/${id}`, method: 'DELETE' });

/* ============================================================
 * /admin/rbac/roles — 角色管理
 * ========================================================== */

export type RoleStatus = 'ACTIVE' | 'DISABLED';

export interface Role {
  id: number;
  code: string;
  name: string;
  description?: string;
  status: RoleStatus;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface RoleCreateRequest {
  code: string;
  name: string;
  description?: string;
  status?: RoleStatus;
  sort_order?: number;
}

export interface RoleUpdateRequest {
  name?: string;
  description?: string;
  status?: RoleStatus;
  sort_order?: number;
}

export interface ListRolesParams {
  search?: string;
  status?: RoleStatus;
  page?: number;
  size?: number;
}

export const listRoles = (params?: ListRolesParams) =>
  request<PageResult<Role>>({ url: '/admin/rbac/roles', method: 'GET', params });

export const getRole = (id: number) =>
  request<Role>({ url: `/admin/rbac/roles/${id}`, method: 'GET' });

export const createRole = (data: RoleCreateRequest) =>
  request<Role>({ url: '/admin/rbac/roles', method: 'POST', data });

export const updateRole = (id: number, data: RoleUpdateRequest) =>
  request<Role>({ url: `/admin/rbac/roles/${id}`, method: 'PUT', data });

export const deleteRole = (id: number) =>
  request<void>({ url: `/admin/rbac/roles/${id}`, method: 'DELETE' });

/* ============================================================
 * 角色 ↔ 菜单绑定
 * ========================================================== */

export const listRoleMenus = (roleId: number) =>
  request<number[]>({ url: `/admin/rbac/roles/${roleId}/menus`, method: 'GET' });

/** 覆盖式：空数组 = 清空 */
export const assignRoleMenus = (roleId: number, menuIds: number[]) =>
  request<void>({
    url: `/admin/rbac/roles/${roleId}/menus`,
    method: 'PUT',
    data: { menu_ids: menuIds },
  });

/* ============================================================
 * 管理员账号 CRUD
 * ========================================================== */

export type AdminUserStatus = 'ACTIVE' | 'DISABLED';
export type AdminUserRole = 'ADMIN' | 'SUPER_ADMIN';

export interface AdminUser {
  id: number;
  username: string;
  display_name?: string;
  email?: string;
  phone?: string;
  role: AdminUserRole;
  status: AdminUserStatus;
  data_scope?: AdminDataScope;
  company_id?: number | null;
  last_login_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ListAdminUsersParams {
  search?: string;
  status?: AdminUserStatus;
  role?: AdminUserRole;
  page?: number;
  size?: number;
}

export interface AdminUserCreateRequest {
  username: string;
  password: string;
  display_name?: string;
  email?: string;
  phone?: string;
  /** 仅允许 ADMIN（默认）；SUPER_ADMIN 必须 DBA 初始化 */
  role?: AdminUserRole;
  data_scope: AdminDataScope;
  /** COMPANY 必填；PLATFORM 不能传（70510） */
  company_id?: number;
}

/** 整体替换；下一次请求即生效，对方不用重登。SUPER_ADMIN 不能改（70508） */
export interface AdminUserDataScopeRequest {
  data_scope: AdminDataScope;
  company_id?: number;
}

export interface AdminUserUpdateRequest {
  display_name?: string;
  email?: string;
  phone?: string;
}

export interface AdminUserPasswordResetRequest {
  new_password: string;
}

export interface AdminUserStatusRequest {
  status: AdminUserStatus;
}

export const listAdminUsers = (params?: ListAdminUsersParams) =>
  request<PageResult<AdminUser>>({
    url: '/admin/rbac/admin-users',
    method: 'GET',
    params,
  });

export const getAdminUser = (id: number) =>
  request<AdminUser>({ url: `/admin/rbac/admin-users/${id}`, method: 'GET' });

export const createAdminUser = (data: AdminUserCreateRequest) =>
  request<AdminUser>({ url: '/admin/rbac/admin-users', method: 'POST', data });

export const updateAdminUser = (id: number, data: AdminUserUpdateRequest) =>
  request<AdminUser>({
    url: `/admin/rbac/admin-users/${id}`,
    method: 'PUT',
    data,
  });

export const resetAdminUserPassword = (id: number, data: AdminUserPasswordResetRequest) =>
  request<void>({
    url: `/admin/rbac/admin-users/${id}/password`,
    method: 'PUT',
    data,
  });

export const setAdminUserStatus = (id: number, status: AdminUserStatus) =>
  request<void>({
    url: `/admin/rbac/admin-users/${id}/status`,
    method: 'PUT',
    data: { status },
  });

export const updateAdminUserDataScope = (id: number, data: AdminUserDataScopeRequest) =>
  request<AdminUser>({
    url: `/admin/rbac/admin-users/${id}/data-scope`,
    method: 'PUT',
    data,
  });

/* ============================================================
 * 操作员 ↔ 角色绑定
 * ========================================================== */

export const listAdminUserRoles = (adminUserId: number) =>
  request<number[]>({
    url: `/admin/rbac/admin-users/${adminUserId}/roles`,
    method: 'GET',
  });

/** 覆盖式：空数组 = 清空。会 evict 该 admin 的权限缓存。 */
export const assignAdminUserRoles = (adminUserId: number, roleIds: number[]) =>
  request<void>({
    url: `/admin/rbac/admin-users/${adminUserId}/roles`,
    method: 'PUT',
    data: { role_ids: roleIds },
  });

/* ============================================================
 * 我的资料 / 密码
 * ========================================================== */

export interface MyProfileUpdateRequest {
  display_name?: string;
  email?: string;
  phone?: string;
}

export interface MyPasswordUpdateRequest {
  old_password: string;
  new_password: string;
}

export const updateMyProfile = (data: MyProfileUpdateRequest) =>
  request<AdminUser>({ url: '/admin/rbac/me/profile', method: 'PUT', data });

export const changeMyPassword = (data: MyPasswordUpdateRequest) =>
  request<void>({ url: '/admin/rbac/me/password', method: 'PUT', data });
